/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';

import { DialogRef, DialogService } from '@ngneat/dialog';
import { FormActionDirective } from '@ngneat/error-tailor';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { SocialAuthService, SocialUser } from 'angularx-social-login';
import firebase from 'firebase/app';
import { AuthenticationService, User, UsersApiService } from 'rest-client';
import { from } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { ActiveCampaignService } from '@app/modules/core/services/active-campaign/active-campaign.service';
import { SetUserAction } from '@app/modules/core/states/auth-state/auth.actions';
import { ReservationStateModel } from '@app/modules/core/states/reservation-state/reservation.model';
import { DIALOG_CONFIG } from '@app/modules/core/utils/dialog.config';
import { GoogleTagManagerService } from '@core/services/analytics/google-tag-manager.service';
import {
  AUTHPROVIDERS,
  PROVIDERS,
  SOCIAL_TOKENS,
} from '@core/utils/social-login.config';
import { RequestResponse, SignUpForm } from '@shared/models';

import { buildUser } from '../../utils/sign-up-dialog.utils';
import { FeedbackDialogComponent } from '../feedback-dialog/feedback-dialog.component';

@UntilDestroy()
@Component({
  selector: 'bkg-sign-up-dialog',
  templateUrl: './sign-up-dialog.component.html',
})
export class SignUpDialogComponent implements OnInit {
  @ViewChild(FormActionDirective) errorTailor: FormActionDirective;

  form: FormGroup;
  errorMessage: string;
  socialUser: SocialUser;
  requestActive: boolean;
  userEmail: string;
  reservationData: ReservationStateModel;
  showContinue: boolean;

  constructor(
    public ref: DialogRef,
    private fb: FormBuilder,
    private socialAuthService: SocialAuthService,
    private usersApiService: UsersApiService,
    private translateService: TranslateService,
    private authService: AuthenticationService,
    private store: Store,
    private dialog: DialogService,
    private googleTagManagerService: GoogleTagManagerService,
    private activeCampaignService: ActiveCampaignService
  ) {}

  get isInvalidForm(): boolean {
    return this.form?.invalid;
  }

  get hasSocialUser(): boolean {
    return !!this.socialUser;
  }

  ngOnInit(): void {
    this.reservationData = this.ref.data as ReservationStateModel;
    this.showContinue = this.ref.data?.showContinue as boolean;
    this.initForm();
    if (this.reservationData?.id) {
      this.form.patchValue(this.patchFromReservation);
    }
  }

  get patchFromReservation(): any {
    return {
      email: this.reservationData.trip.email,
      name: this.reservationData.guestName,
      surname: this.reservationData.guestSurname,
    };
  }

  private initForm(): void {
    this.form = this.fb.group({
      name: [null, [Validators.required, Validators.maxLength(150)]],
      surname: [null, [Validators.required, Validators.maxLength(150)]],
      email: [null, [Validators.required, Validators.email]],
      password: [null, [Validators.required, Validators.minLength(8)]],
      privacyPolicy: [false, Validators.requiredTrue],
      acceptPromos: false,
      acceptNewsletter: false,
    });
  }

  onCloseDialog(): void {
    this.ref.close();
  }

  onCloseErrorBanner(): void {
    this.errorMessage = null;
  }

  navigateToLogin(): void {
    this.ref.close(true);
  }

  onLoadFacebookData(): void {
    const provider = new firebase.auth.FacebookAuthProvider();
    provider.addScope('email');
    firebase
      .auth()
      .signInWithPopup(provider)
      .then((result: any) => {
        const credential = result.credential;
        const userData = result.additionalUserInfo.profile;
        const thirdPartyData = {
          email: userData['email'],
          id: userData['id'],
          idName: AUTHPROVIDERS.facebook,
          lastName: userData['last_name'],
          name: userData['fist_name'],
          provider: PROVIDERS.facebook,
          token: credential['accessToken'],
          tokenName: SOCIAL_TOKENS.facebook,
        };
        this.initSocialRegister(thirdPartyData);
      })
      .catch((err: HttpErrorResponse) => {
        this.onRequestError(err?.error);
      });
  }

  onLoadGoogleData(): void {
    const provider = new firebase.auth.GoogleAuthProvider();
    provider.addScope('profile');
    provider.addScope('email');
    firebase
      .auth()
      .signInWithPopup(provider)
      .then((result: any) => {
        const credential = result.credential;
        const userData = result.additionalUserInfo.profile;
        const thirdPartyData = {
          email: userData['email'],
          id: userData['id'],
          idName: AUTHPROVIDERS.google,
          lastName: userData['family_name'],
          name: userData['given_name'],
          provider: PROVIDERS.google,
          token: credential['idToken'],
          tokenName: SOCIAL_TOKENS.google,
        };
        this.initSocialRegister(thirdPartyData);
      })
      .catch((err: HttpErrorResponse) => {
        this.onRequestError(err?.error);
      });
  }

  onLoadAppleData(): void {
    const provider = new firebase.auth.OAuthProvider('apple.com');
    firebase
      .auth()
      .signInWithPopup(provider)
      .then((result: any) => {
        const credential = result.credential;
        const userData = result.additionalUserInfo.profile;
        const thirdPartyData = {
          email: userData['email'],
          id: userData['id'],
          idName: AUTHPROVIDERS.apple,
          lastName: userData['family_name'],
          name: userData['given_name'],
          provider: PROVIDERS.apple,
          token: credential['idToken'],
          tokenName: SOCIAL_TOKENS.apple,
        };
        if (!thirdPartyData.email) {
          const splitToken = thirdPartyData.token.split('.');
          const appleObject = JSON.parse(atob(splitToken[1]));
          thirdPartyData.email = appleObject.email;
        }
        this.initSocialRegister(thirdPartyData);
      })
      .catch((err: HttpErrorResponse) => {
        this.onRequestError(err?.error);
      });
  }

  private initSocialRegister(thirdPartyData: any): void {
    const newUser = this.createUserFromThird(thirdPartyData);
    this.usersApiService
      .createUser(newUser)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .then((newUserReturn: User) => {
        this.googleTagManagerService.signUpEvent(thirdPartyData.provider);
        this.activeCampaignService.setACEmail(thirdPartyData.email);
        return this.loginThirdPartyFromRegister(thirdPartyData);
      })
      .catch((err: HttpErrorResponse) => {
        this.onRequestError(err?.error);
      });
  }

  private createUserFromThird(thirdPartyData: any): User {
    const newUser = new User();

    newUser.email = thirdPartyData.email;
    newUser.name = thirdPartyData?.name ? thirdPartyData.name : '';
    newUser.lastName = thirdPartyData.lastName ? thirdPartyData.lastName : '';
    newUser.phone = null;
    newUser.zipCode = null;
    newUser.language = this.translateService.currentLang;
    newUser.accept_promos = false;
    newUser.accept_newsletter = false;
    newUser.provider = thirdPartyData.provider.toLocaleLowerCase();
    if (thirdPartyData.tokenName === SOCIAL_TOKENS.apple) {
      newUser.token_apple = thirdPartyData.token;
      newUser.auth_id_apple = thirdPartyData.id;
    } else if (thirdPartyData.tokenName === SOCIAL_TOKENS.google) {
      newUser.token_google = thirdPartyData.token;
      newUser.auth_id_google = thirdPartyData.id;
    } else if (thirdPartyData.tokenName === SOCIAL_TOKENS.facebook) {
      newUser.token_facebook = thirdPartyData.token;
      newUser.auth_id_facebook = thirdPartyData.id;
    }
    return newUser;
  }

  private loginThirdPartyFromRegister(thirdPartyData: any): void {
    from(
      this.authService.loginSSO(
        thirdPartyData.tokenName,
        thirdPartyData.token,
        thirdPartyData.provider.toLocaleLowerCase()
      )
    )
      .pipe(untilDestroyed(this))
      .subscribe(
        () => this.onSuccessLogin(thirdPartyData),
        () => {
          this.disableControl('password');
          this.loadSocialUserData();
        }
      );
  }

  onCancelSocialUser(): void {
    this.socialUser = null;
    this.socialAuthService.signOut();
    this.enableControl('password');
    this.form.reset();
    this.errorTailor.element.reset();
  }

  onContinue(): void {
    this.googleTagManagerService.pushOnDataLayer({
      event: 'booking-user-no-register',
    });
    this.googleTagManagerService.pushBookingActionOnDataLayer({
      event_name: 'booking-user-no-register',
    });
    this.ref.close();
  }

  onCreateUser(): void {
    const signUpForm = this.form.value as SignUpForm;
    const lang = this.translateService.currentLang;
    const user = buildUser(signUpForm, this.socialUser, lang);
    let reservationId = null;
    if (this.reservationData?.id) {
      reservationId = this.reservationData.id;
    }

    this.requestActive = true;
    from(this.usersApiService.createUser(user, undefined, reservationId))
      .pipe(
        finalize(() => (this.requestActive = false)),
        untilDestroyed(this)
      )
      .subscribe(
        () => this.onSuccessCreateUser(signUpForm, this.socialUser),
        (error: HttpErrorResponse) => this.onRequestError(error?.error)
      );
  }

  hasErrors(fieldName: string): boolean {
    const control = this.form.get(fieldName);

    return !!control?.invalid && !!control.touched;
  }

  isValidField(fieldName: string): boolean {
    const control = this.form.get(fieldName);

    return !!control?.valid;
  }

  private disableControl(controlName: string): void {
    const passwordControl = this.getControl(controlName);

    passwordControl.reset();
    passwordControl.disable();
  }

  private enableControl(controlName: string): void {
    const passwordControl = this.getControl(controlName);

    passwordControl.reset();
    passwordControl.enable();
    passwordControl.markAsUntouched();
    passwordControl.updateValueAndValidity();
  }

  private loadSocialUserData(): void {
    const controlName = this.getControl('name');
    const controlSurname = this.getControl('surname');
    const controlEmail = this.getControl('email');

    controlName.patchValue(this.socialUser?.name);
    controlSurname.patchValue(this.socialUser?.lastName);
    controlEmail.patchValue(this.socialUser?.email);
  }

  private getControl(controlName: string): AbstractControl {
    return this.form.get(controlName);
  }

  private onRequestError(error: RequestResponse): void {
    this.errorMessage =
      error?.reason ||
      (this.translateService.instant(
        'sharedModule.bkgSignUpDialog.createError'
      ) as string);
  }

  private socialLogin(socialToken: string, socialUser: SocialUser): void {
    const authToken =
      socialToken === SOCIAL_TOKENS.google
        ? socialUser.idToken
        : socialUser.authToken;

    from(
      this.authService.loginSSO(
        socialToken,
        authToken,
        socialUser.provider.toLocaleLowerCase()
      )
    )
      .pipe(untilDestroyed(this))
      .subscribe(
        () => this.onSuccessLogin({
          socialToken: socialToken,
          authToken: authToken,
          provider: socialUser.provider,
        }),
        () => {
          this.socialUser = socialUser;
          this.disableControl('password');
          this.loadSocialUserData();
        }
      );
  }

  private socialLoginApple(
    socialToken: string,
    authToken: string,
    provider: string,
    appleResponse: any
  ): void {
    from(this.authService.loginSSO(socialToken, authToken, provider)).subscribe(
      () => this.onSuccessLogin({
        socialToken: socialToken,
        authToken: authToken,
        provider: provider,
      }),
      () => {
        this.socialUser = new SocialUser();
        this.socialUser.authToken = authToken;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        this.socialUser.email =
          appleResponse?.additionalUserInfo?.profile?.email;
        this.disableControl('password');
        this.loadSocialUserData();
      }
    );
  }

  private login(email: string, password: string): void {
    from(this.authService.login(email, btoa(password)))
      .pipe(untilDestroyed(this))
      .subscribe(
        () => this.onSuccessLogin(),
        (err: HttpErrorResponse) => this.onRequestError(err?.error)
      );
  }

  private onSuccessLogin(thirdPartyData?: any): void {

    if (this.authService.isTokenValid()) {
      const socialToken = (thirdPartyData?.socialToken) ? thirdPartyData.socialToken : null;
      const provider = (thirdPartyData?.provider) ? thirdPartyData.provider : 'email';
      this.googleTagManagerService.signUpEvent(provider);
      if (socialToken && socialToken === SOCIAL_TOKENS.google) {
        this.googleTagManagerService.signUpEvent('GOOGLE');
        this.googleTagManagerService.pushOnDataLayer({
          event: 'booking-user-register-gmail',
        });
        this.googleTagManagerService.pushBookingActionOnDataLayer({
          event_name: 'booking-user-register-gmail',
        });
      } else if (socialToken && socialToken === SOCIAL_TOKENS.facebook) {
        this.googleTagManagerService.signUpEvent('FACEBOOK');
        this.googleTagManagerService.pushOnDataLayer({
          event: 'booking-user-register-facebook',
        });
        this.googleTagManagerService.pushBookingActionOnDataLayer({
          event_name: 'booking-user-register-facebook',
        });
      } else if (socialToken && socialToken === SOCIAL_TOKENS.apple) {
        this.googleTagManagerService.signUpEvent('APPLE');
        this.googleTagManagerService.pushOnDataLayer({
          event: 'booking-user-register-apple',
        });
        this.googleTagManagerService.pushBookingActionOnDataLayer({
          event_name: 'booking-user-register-apple',
        });
      }else {
        this.googleTagManagerService.pushOnDataLayer({
          event: 'booking-user-register-email',
        });
        this.googleTagManagerService.pushBookingActionOnDataLayer({
          event_name: 'booking-user-register-email',
        });
      }

      this.usersApiService
        .getUser()
        .then((user: User) => {
          this.store.dispatch(new SetUserAction(user));
          if (this.userEmail) {
            this.dialog.open(FeedbackDialogComponent, {
              ...DIALOG_CONFIG,
              size: 'md',
              enableClose: false,
              data: {
                icon: 'assets/images/icons/ok-icon.svg',
                title: this.translateService.instant(
                  'sharedModule.bkgFeedbackDialog.registerTitle'
                ) as string,
                body: this.translateService.instant(
                  'sharedModule.bkgFeedbackDialog.registerBody',
                  { email: this.userEmail }
                ) as string,
              },
            });
          }
          this.ref.close();
        })
        .catch((err: HttpErrorResponse) => {
          this.onRequestError(err?.error);
        });
    } else {
      this.errorMessage = 'Invalid token';
    }
  }

  private onSuccessCreateUser(form: SignUpForm, socialUser: SocialUser): void {
    this.googleTagManagerService.signUpEvent((socialUser)? socialUser.provider : 'email');
    this.activeCampaignService.setACEmail(form.email);
    // socialUsers do not trigger this function ! check initSocialRegister
    if (socialUser) {
      const socialToken =
        socialUser.provider === PROVIDERS.google
          ? SOCIAL_TOKENS.google
          : SOCIAL_TOKENS.facebook;

      this.socialLogin(socialToken, socialUser);
    } else {
      this.userEmail = form.email;
      this.login(form.email, form.password);
    }

  }

  get isIos(): boolean {
    const is_OSX = /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform);
    const is_iOS = /(iPhone|iPod|iPad)/i.test(navigator.platform);

    const is_Mac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    const is_iPhone = navigator.platform === 'iPhone';
    const is_iPod = navigator.platform === 'iPod';
    const is_iPad = navigator.platform === 'iPad';

    return is_OSX || is_iOS || is_Mac || is_iPhone || is_iPod || is_iPad;
  }
}
